/**
 * Provider-aware cascading filters for currency/network/payment method selection.
 *
 * Pure functions with no React deps — testable independently.
 * Uses the `providers` arrays from the backend's AggregatedConfig to ensure
 * only provider-supported combinations are selectable in the form.
 */

import type {
  SupportedFiat,
  SupportedCrypto,
  SupportedPaymentMethod,
} from '@nowramp/sdk';

/**
 * Build-time compat flag. When true (default), undefined providers arrays
 * are treated as wildcards (match everything). Set to false and rebuild
 * once all providers are fully annotated to enable strict filtering.
 */
export const PROVIDERS_WILDCARD_COMPAT = true;

/**
 * Returns true if two providers arrays share at least one common provider.
 *
 * Semantics:
 *   undefined → wildcard when compat is true, else no match
 *   []        → "no providers support this", always returns false
 */
export function providersOverlap(
  a?: string[],
  b?: string[],
  compat = PROVIDERS_WILDCARD_COMPAT,
): boolean {
  if (a === undefined || b === undefined) return compat;
  if (a.length === 0 || b.length === 0) return false;
  return a.some((p) => b.includes(p));
}

/** Filter fiats by provider overlap with selected network. */
export function filterAvailableFiats(
  fiats: SupportedFiat[],
  networkProviders: string[] | undefined,
  compat = PROVIDERS_WILDCARD_COMPAT,
): SupportedFiat[] {
  return fiats.filter((f) => providersOverlap(f.providers, networkProviders, compat));
}

/**
 * Filter cryptos: keep only networks with provider overlap against fiat providers.
 * Drop cryptos with zero remaining networks.
 */
export function filterAvailableCryptos(
  cryptos: SupportedCrypto[],
  fiatProviders: string[] | undefined,
  compat = PROVIDERS_WILDCARD_COMPAT,
): SupportedCrypto[] {
  const result: SupportedCrypto[] = [];
  for (const crypto of cryptos) {
    const filteredNetworks = crypto.networks.filter((n) =>
      providersOverlap(n.providers, fiatProviders, compat),
    );
    if (filteredNetworks.length > 0) {
      result.push(
        filteredNetworks.length === crypto.networks.length
          ? crypto
          : { ...crypto, networks: filteredNetworks },
      );
    }
  }
  return result;
}

/** Filter payment methods by provider overlap with network AND supportedFiats. */
export function filterAvailablePaymentMethods(
  methods: SupportedPaymentMethod[],
  networkProviders: string[] | undefined,
  fiatCurrency: string,
  compat = PROVIDERS_WILDCARD_COMPAT,
): SupportedPaymentMethod[] {
  if (!fiatCurrency) return [];
  return methods.filter(
    (m) =>
      m.supportedFiats.includes(fiatCurrency) &&
      providersOverlap(m.providers, networkProviders, compat),
  );
}

// =============================================================================
// Reconciliation
// =============================================================================

export interface ReconciledSelections {
  cryptoCurrency?: string;
  network?: string;
  fiatCurrency?: string;
  paymentMethodId?: string;
}

export interface LockFlags {
  cryptoLocked?: boolean;
  networkLocked?: boolean;
  fiatLocked?: boolean;
}

/**
 * Pure reconciliation: given current selections, filtered lists, and lock flags,
 * returns a plain object with corrected values (or null if no change needed).
 */
export function reconcileSelections(
  state: {
    cryptoCurrency: string;
    network: string;
    fiatCurrency: string;
    paymentMethodId: string;
  },
  filtered: {
    availableFiats: SupportedFiat[];
    availableCryptos: SupportedCrypto[];
    availablePaymentMethods: SupportedPaymentMethod[];
  },
  locks: LockFlags,
): ReconciledSelections | null {
  const result: ReconciledSelections = {};
  let changed = false;

  // --- Crypto + Network ---
  if (!locks.cryptoLocked && !locks.networkLocked) {
    // Both unlocked: validate crypto+network pair, correct both if invalid
    const cryptoValid =
      state.cryptoCurrency &&
      filtered.availableCryptos.some(
        (c) =>
          c.code === state.cryptoCurrency &&
          c.networks.some((n) => n.id === state.network),
      );
    if (!cryptoValid && filtered.availableCryptos.length > 0) {
      // Prefer BTC on Bitcoin network as the default
      const btc = filtered.availableCryptos.find((c) => c.code === 'BTC');
      const first = btc || filtered.availableCryptos[0];
      const net =
        first.networks.find((n) => n.isDefault) || first.networks[0];
      if (first.code !== state.cryptoCurrency) {
        result.cryptoCurrency = first.code;
        changed = true;
      }
      if (net && net.id !== state.network) {
        result.network = net.id;
        changed = true;
      }
    }
  } else if (!locks.cryptoLocked && locks.networkLocked) {
    // Network locked, crypto unlocked: pick a crypto that supports the locked network
    const cryptoValid =
      state.cryptoCurrency &&
      filtered.availableCryptos.some(
        (c) =>
          c.code === state.cryptoCurrency &&
          c.networks.some((n) => n.id === state.network),
      );
    if (!cryptoValid) {
      const compatible = filtered.availableCryptos.find((c) =>
        c.networks.some((n) => n.id === state.network),
      );
      if (compatible && compatible.code !== state.cryptoCurrency) {
        result.cryptoCurrency = compatible.code;
        changed = true;
      }
    }
  } else if (locks.cryptoLocked && !locks.networkLocked) {
    // Crypto locked, network unlocked: correct network against locked crypto's available networks
    const lockedCrypto = filtered.availableCryptos.find(
      (c) => c.code === state.cryptoCurrency,
    );
    if (lockedCrypto) {
      const networkValid = lockedCrypto.networks.some(
        (n) => n.id === state.network,
      );
      if (!networkValid && lockedCrypto.networks.length > 0) {
        const net =
          lockedCrypto.networks.find((n) => n.isDefault) ||
          lockedCrypto.networks[0];
        if (net.id !== state.network) {
          result.network = net.id;
          changed = true;
        }
      }
    }
  }
  // Both locked: no corrections possible

  // --- Fiat (prefer USD, else first available) ---
  if (!locks.fiatLocked) {
    const fiatValid =
      state.fiatCurrency &&
      filtered.availableFiats.some((f) => f.code === state.fiatCurrency);
    if (!fiatValid && filtered.availableFiats.length > 0) {
      const usd = filtered.availableFiats.find((f) => f.code === 'USD');
      const target = usd || filtered.availableFiats[0];
      if (target.code !== state.fiatCurrency) {
        result.fiatCurrency = target.code;
        changed = true;
      }
    }
  }

  // --- Payment Method (prefer card/debit, else first) ---
  const methodValid =
    state.paymentMethodId &&
    filtered.availablePaymentMethods.some(
      (m) => m.id === state.paymentMethodId,
    );
  if (!methodValid && filtered.availablePaymentMethods.length > 0) {
    const card = filtered.availablePaymentMethods.find(
      (m) => m.id.includes('card') || m.id.includes('debit'),
    );
    const target = card || filtered.availablePaymentMethods[0];
    if (target.id !== state.paymentMethodId) {
      result.paymentMethodId = target.id;
      changed = true;
    }
  }

  return changed ? result : null;
}

// =============================================================================
// Selection Unsupported Check
// =============================================================================

/**
 * Returns true when a locked selection has no provider support in the current flow.
 * Drives CTA disable + message in the form.
 */
export function isSelectionUnsupported(
  config: { cryptos?: SupportedCrypto[]; fiats?: SupportedFiat[] },
  state: { cryptoCurrency: string; network: string; fiatCurrency: string },
  locks: LockFlags,
  filtered: {
    availableFiats: SupportedFiat[];
    availableCryptos: SupportedCrypto[];
    availablePaymentMethods: SupportedPaymentMethod[];
  },
): boolean {
  // A. Locked crypto+network
  if (locks.cryptoLocked) {
    const crypto = config.cryptos?.find(
      (c) => c.code === state.cryptoCurrency,
    );
    if (!crypto) return true;
    const network = crypto.networks?.find((n) => n.id === state.network);
    if (!network) return true;
    if (network.providers !== undefined && network.providers.length === 0)
      return true;
    // Has explicit providers but no fiats or payment methods overlap
    if (
      network.providers !== undefined &&
      (filtered.availableFiats.length === 0 ||
        filtered.availablePaymentMethods.length === 0)
    )
      return true;
  }

  // B. Locked network only (not crypto-locked)
  if (locks.networkLocked && !locks.cryptoLocked) {
    const hasNetworkSupport = filtered.availableCryptos.some((c) =>
      c.networks.some((n) => n.id === state.network),
    );
    if (!hasNetworkSupport) return true;
  }

  // C. Locked fiat
  if (locks.fiatLocked) {
    const fiat = config.fiats?.find((f) => f.code === state.fiatCurrency);
    if (!fiat) return true;
    if (fiat.providers !== undefined && fiat.providers.length === 0)
      return true;
    if (
      fiat.providers !== undefined &&
      filtered.availableCryptos.length === 0
    )
      return true;
  }

  return false;
}
